import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle } from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import { toast } from "react-toastify";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";
import RentalFilePreview from "./RentalFilePreview";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT =
  "image/gif,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf,.pdf";

const addIdBtnClass =
  "w-full rounded-lg border border-[#E5E7EB] bg-white py-3 text-sm font-semibold text-[#000929] shadow-sm transition-colors hover:bg-[#F9FAFB]";

const IdentityDocuments = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection, uploadDocument } =
    useRentalProfile();
  const photoIdRef = useRef(null);
  const secondIdRef = useRef(null);
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [photoIdFile, setPhotoIdFile] = useState(null);
  const [secondIdFile, setSecondIdFile] = useState(null);

  useEffect(() => {
    if (loading) return;
    setPhotoIdFile(null);
    setSecondIdFile(null);
  }, [loading, profile.identityDocuments]);

  const validateAndSet = (file, setter) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File must be 10MB or smaller.");
      return;
    }
    setter(file);
  };

  const handlePhotoIdChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    validateAndSet(f, setPhotoIdFile);
  };

  const handleSecondIdChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    validateAndSet(f, setSecondIdFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let photoId = profile.identityDocuments.photoId || null;
    let secondId = profile.identityDocuments.secondId || null;

    if (photoIdFile) {
      photoId = await uploadDocument(photoIdFile);
      if (!photoId) return;
    }
    if (secondIdFile) {
      secondId = await uploadDocument(secondIdFile);
      if (!secondId) return;
    }
    if (!photoId && !secondId) {
      toast.error("Please upload at least one ID document.");
      return;
    }

    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.IDENTITY_DOCUMENTS, {
      photoId,
      secondId,
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Identity documents
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-[#000929]">
                Add your ID
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Upload images of two IDs. At least one ID must have your photo
                on it.
              </p>
            </div>

            <input
              ref={photoIdRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={handlePhotoIdChange}
            />
            <input
              ref={secondIdRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={handleSecondIdChange}
            />

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => photoIdRef.current?.click()}
                className={addIdBtnClass}
              >
                Add photo ID
              </button>
              <RentalFilePreview
                label="Photo ID"
                file={photoIdFile}
                saved={photoIdFile ? null : profile.identityDocuments.photoId}
                onRemove={photoIdFile ? () => setPhotoIdFile(null) : undefined}
              />
              <button
                type="button"
                onClick={() => secondIdRef.current?.click()}
                className={addIdBtnClass}
              >
                Add second ID
              </button>
              <RentalFilePreview
                label="Second ID"
                file={secondIdFile}
                saved={secondIdFile ? null : profile.identityDocuments.secondId}
                onRemove={secondIdFile ? () => setSecondIdFile(null) : undefined}
              />
            </div>

            <div className="border-t border-[#E5E7EB] pt-8">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-semibold text-[#097FE6] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="identityDocuments"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default IdentityDocuments;
